import { Box, Button, Typography } from '@mui/material';
import {
  Band,
  GroupShell,
  LedgerHeader,
  Money,
} from '../../../shared/components/ledger';
import { ACTIVITY_COLLAPSE, ACTIVITY_COLUMNS } from './columns';
import { ActivityLedgerRow } from './ActivityLedgerRow';
import type {
  ActivityLedgerRowModel,
  ActivityWeekGroup,
} from './toActivityRows';

interface ActivityLedgerProps {
  weeks: ActivityWeekGroup[];
  /**
   * Only the weeks the user explicitly collapsed, so weeks arriving with the
   * next page open by default.
   */
  collapsedWeeks: ReadonlySet<string>;
  onToggleWeek: (key: string) => void;
  onOpenRow: (row: ActivityLedgerRowModel) => void;
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

export function ActivityLedger({
  weeks,
  collapsedWeeks,
  onToggleWeek,
  onOpenRow,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: ActivityLedgerProps) {
  return (
    <>
      <LedgerHeader columns={ACTIVITY_COLUMNS} collapse={ACTIVITY_COLLAPSE} />
      {weeks.map(week => (
        <GroupShell
          key={week.key}
          open={!collapsedWeeks.has(week.key)}
          onToggle={() => onToggleWeek(week.key)}
          header={(open, toggle) => (
            <Band
              collapse={ACTIVITY_COLLAPSE}
              primary={week.label}
              secondary={week.range}
              count={week.rows.length}
              open={open}
              onToggle={toggle}
              trailing={<Money value={week.total} />}
              data-testid="activity-group-header"
            />
          )}
        >
          {week.rows.map(row => (
            <ActivityLedgerRow
              key={row.id}
              row={row}
              columns={ACTIVITY_COLUMNS}
              collapse={ACTIVITY_COLLAPSE}
              onOpen={onOpenRow}
            />
          ))}
        </GroupShell>
      ))}
      {hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
      <Box sx={{ textAlign: 'center', py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {totalCount} booking{totalCount !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </>
  );
}
